
// 3rd Party
import {
    PropType,
} from 'vue';
import {
    debounce,
} from 'vue-debounce';

// Keys
import {
    PersistentStorageInjectionKey,
} from '@shared/config/keys';

type Id = string;

type Data = Record<string, unknown> | Record<string, unknown>[];

type Format = Record<string, string>;

type Filter = {
    id: Id;
    data: Data;
    index?: number;
    format?: Format;
}

type SelectedFiltersProp = {
    override: boolean;
    filters: Omit<Filter, 'index'>[];
};

export default {
    inject: {
        persistentStorage: {
            from: PersistentStorageInjectionKey,
        },
    },
    props: {
        debounceTime: {
            type: Number,
            default: 0,
        },
        storageKey: {
            type: String,
            default: '',
        },
        selectedFilters: {
            type: Object as PropType<SelectedFiltersProp>,
            default: () => ({}),
        },
    },
    data() {
        return {
            activeFilters: [] as Filter[],
            paramMap: {} as Record<string, string | string[]>,
            emitFilters: this.setupEmit(),
            addFilters: null,
        };
    },
    watch: {
        activeFilters(updatedFilters: Filter[]) {
            if (!this.storageKey) {
                return;
            }

            this.persistentStorage.set(this.storageKey, updatedFilters);
        },

        // Filters passed as props to pre-select or update. Override flag is used to determine whether the provided filters override the existing ones
        selectedFilters({
            override,
            filters,
        }: SelectedFiltersProp) {
            if (!filters.length) {
                return;
            }

            if (override) {
                this.activeFilters = [];
            }

            for (const filter of filters) {
                this.addFilters(filter, true);
            }

            this.emitFilters();
        },
    },
    mounted() {
        const {
            override,
            filters = [],
        } = (this.selectedFilters ?? {});

        this.activeFilters = override ? [] : this.persistentStorage.getObject(this.storageKey, []);
        const biggestIndex = Math.max(...this.activeFilters.map(({ index = 0 }) => index));

        this.addFilters = this.setupAddFilters(biggestIndex + 1);

        // Active filters will be empty in case override is set to true or we don't have any stored in LS. Default filters are considered here
        if (!this.activeFilters.length) {
            for (const filter of filters) {
                this.addFilters(filter, true);
            }

            return;
        }

        for (const {
            data,
            format,
        } of this.activeFilters) {
            if (format) {
                this.addToParamMap(data, format);
            }
        }

        this.emitFilters();
    },
    methods: {
        // Setting up a closure to ensure debounce can retain reference to the same timeout
        setupEmit(): () => void {
            const emitCallback = () => this.$emit('filtersChange', this.paramMap);
            const debouncedCallback = debounce(emitCallback, this.debounceTime);
            const emitFn = this.debounceTime > 0
                ? () => {
                    // Emitting a secondary event in case parent needs to be notified of filter interaction before debounced request
                    this.$emit('filtersInteraction');
                    debouncedCallback();
                } : emitCallback;

            return emitFn;
        },

        getFormattedRequestData(data: Data, format: Format): Record<string, unknown> {
            return this.toArray(data).reduce((acc, dataObj) => {
                const fragment = Object.entries(format).reduce((innerAcc, [key, value]: [string, string]) => {
                    // Getting the placeholder prop name. Ex: {min} returns min
                    const placeholder = value.match(/{(.*?)}/g)[0];

                    // Checking whether it should consist of multiple values. Ex: format as 'batchTypes': [{value}] -> batchTypes[]=topup&batchTypes[]=withdraw
                    const isMultiValue = value.search(/(\[.*?\])/g) >= 0;

                    // Getting the paths to access the value. Ex: {value.min} -> We need to access the prop 'min' defined inside 'value' object
                    const splitProps = placeholder.replace(/[{}]/g, '').split('.');

                    // Traversing the data object to access the child prop defined by splitProps
                    const valueToReplace = splitProps.reduce((acc, prop) => acc?.[prop], dataObj);

                    return valueToReplace
                        ? {
                            ...innerAcc,
                            [key]: isMultiValue ? [
                                ...(acc[key] ?? []) as unknown[],
                                valueToReplace,
                            ] : valueToReplace,
                        } : innerAcc;

                }, {});

                return {
                    ...acc,
                    ...fragment,
                };
            }, {});
        },

        setupAddFilters(startingIndex: number): (filter: Omit<Filter, 'index'>, lazy?: boolean) => void {
            // Index which always keeps on incrementing for the lifecycle of the component
            let index = startingIndex;

            return ({
                id,
                data,
                format,
            }, lazy) => {
                // Setting a map with the stringified data as the key and index as the value. Stringified data as key is used for quick lookup
                const existingDataMap = this.activeFilters.reduce((acc, {
                    data,
                    index,
                    id: filterId,
                }) => {
                    if (filterId === id) {
                        return acc.set(JSON.stringify(data), index);
                    }

                    return acc;
                }, new Map());

                // Returning the indices to keep for the existing filters with the same group ID. New data is the newly added data which is not already selected
                const {
                    indicesToKeep = [],
                    newData = [],
                } = this.toArray(data).reduce((acc, dataObj) => {
                    const existingIndex = existingDataMap.get(JSON.stringify(dataObj));

                    if (existingIndex) {
                        acc.indicesToKeep.set(existingIndex);

                        return acc;
                    }

                    return {
                        ...acc,
                        newData: [
                            ...acc.newData,
                            {
                                id,
                                data: dataObj,
                                format,
                                index: index++,
                            },
                        ],
                    };
                }, {
                    indicesToKeep: new Map(),
                    newData: [],
                });

                // Filtering out any filters of the same ID which were removed from the filter emitting the event
                this.activeFilters = [
                    ...this.activeFilters.filter(({
                        id: filterId,
                        index: filterIndex,
                    }) => id !== filterId || indicesToKeep.has(filterIndex)),
                    ...newData,
                ];

                // Refrain from emitting the filtersChange event in case lazy is set to true
                if (!lazy) {
                    this.emitFilters();
                }

                if (!format) {
                    return;
                }

                this.addToParamMap(data, format);
            };
        },

        addToParamMap(data: Data, format: Format) {
            // Remove the existing parameters matching the format from the config
            this.removeParamsFromRequestConfig(format);

            // Re-add the params with the updated values, if eligible
            this.paramMap = {
                ...this.paramMap,
                ...this.getFormattedRequestData(data, format),
            };
        },

        // Returning filter data as an array
        getFilterDataById(id: Id): Record<string, unknown>[] {
            return this.activeFilters.reduce((acc, {
                id: filterId,
                data,
            }) => (filterId === id ? [...(acc ?? []), data] : acc), []);
        },

        removeParamsFromRequestConfig(format: Format): void {
            for (const key in format) {
                if (this.paramMap[key]) {
                    delete this.paramMap[key];
                }
            }
        },

        // Removing the whole filter group via ID
        removeFilterGroupById(id: Id, lazy?: boolean): void {
            this.activeFilters = this.activeFilters.reduce((acc, filter) => {
                if (id === filter.id) {
                    this.removeParamsFromRequestConfig(filter.format);

                    return acc;
                }

                return [...acc, filter];
            }, []);

            if (lazy) {
                return;
            }

            this.emitFilters();
        },

        removeFilter(filter: Filter, lazy?: boolean): void {
            const {
                index: indexToRemove,
                id,
                format,
            } = filter;

            this.activeFilters = this.activeFilters.filter(({ index }) => index !== indexToRemove);

            // Updating the parameter map with the new data
            if (format) {
                const filterGroupData = this.getFilterDataById(id);

                this.addToParamMap(filterGroupData, format);
            }

            if (lazy) {
                return;
            }

            this.emitFilters();
        },

        resetAllFilters(lazy?: boolean): void {
            this.persistentStorage.remove(this.storageKey);
            this.paramMap = {};
            this.activeFilters = [];

            if (lazy) {
                return;
            }

            this.emitFilters();
        },

        toArray(value: unknown): unknown[] {
            return Array.isArray(value) ? value : [value];
        },
    },
    render() {
        return this.$scopedSlots.default({
            activeFilters: this.activeFilters,
            getFilterDataById: this.getFilterDataById,
            addFilters: this.addFilters,
            paramMap: this.paramMap,
            emitFilters: this.emitFilters,
            removeFilter: this.removeFilter,
            removeFilterGroupById: this.removeFilterGroupById,
            resetAllFilters: this.resetAllFilters,
        });
    },
};
